import { css, Theme } from '@emotion/react';
import {
  AccountCircle,
  AdminPanelSettings,
  Campaign,
  ManageAccounts,
  PowerSettingsNew,
  School,
  SupervisedUserCircle,
} from '@mui/icons-material';
import { Box, Divider, ListItem, ListItemIcon, ListItemText, MenuList } from '@mui/material';
import { useCommunityContext } from 'providers';
import { Fragment, ReactNode, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { useAppAuth } from 'providers/useAppAuth';
import { Popover } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import {
  useCurrentUser,
  useCurrentUserOrganizations,
  useCurrentUserCommunities,
  useFeatureEnabled,
  useResponsive,
  useCurrentUserRoles,
} from 'shared/hooks';

import {
  HeaderNavigationUserProfile,
  HeaderNavigationUserProfileProps,
} from './HeaderNavigationUserProfile';

export interface HeaderNavigationPopoverProps extends HeaderNavigationUserProfileProps {
  isAdminRoute?: boolean;
  target: ReactNode;
}

const ADVERTISING_DOMAIN = process.env.REACT_APP_ADVERTISING_DOMAIN;
const CONTENT_CONTAINER_SIZE = 300;

const userProfileContainerCss = css`
  padding: 15px 25px;
  overflow: hidden;
  width: 100%;
`;

const commonNavItemCss = css`
  font-size: 16px;
  line-height: 26px;
  padding-left: 25px;
  padding-top: 10px;
  padding-bottom: 10px;
`;

const accountNavItemCss = (theme: Theme) => css`
  ${commonNavItemCss};
  color: ${theme.palette.text.secondary};
`;

const signOutNavItemCss = (theme: Theme) => css`
  ${commonNavItemCss};
  color: ${theme.palette.secondary.light};
`;

export function HeaderNavigationPopover({
  isAdminRoute,
  userLoading,
  target,
}: HeaderNavigationPopoverProps) {
  const { user, isAdmin: hasAdminRole } = useCurrentUser();
  const { isPlatformAdmin } = useCurrentUserRoles();
  const { isMobile } = useResponsive();
  const { data: organizations } = useCurrentUserOrganizations();
  const { enabled: showSchoolSwitch } = useFeatureEnabled('showSchoolSwitch');
  const { selectedCommunity, selectCommunity } = useCommunityContext();
  const { logout } = useAppAuth();
  const { push: navigate } = useHistory();

  const { data: communities } = useCurrentUserCommunities();

  const switchableCommunities = useMemo(() => {
    return communities.filter(community => community.id !== selectedCommunity?.id);
  }, [communities, selectedCommunity]);

  const showSwitchCommunityAdministration = useMemo(() => {
    return hasAdminRole;
  }, [hasAdminRole]);

  const showSwitchAdvertising = useMemo(() => {
    return organizations.length > 0 && !isMobile;
  }, [organizations, isMobile]);

  const showSwitchPlatformAdmin = useMemo(() => {
    return isPlatformAdmin;
  }, [isPlatformAdmin]);

  const handleLogout = () => {
    logout();
  };

  const createHandleSwitchSchool = (id: string) => async () => {
    await selectCommunity(id);
    // Refresh the page to avoid inconsistent state and permissions.
    navigate('/');
  };

  return (
    <Popover
      target={target}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      elevation={4}
    >
      {popover => (
        <MenuList style={{ width: CONTENT_CONTAINER_SIZE }} disablePadding>
          <Box css={userProfileContainerCss}>
            <HeaderNavigationUserProfile userLoading={userLoading} user={user} isPopover />
          </Box>

          <Divider />

          {showSchoolSwitch && switchableCommunities.length > 0 && (
            <Fragment>
              {switchableCommunities.map(school => (
                <ListItem
                  onClick={createHandleSwitchSchool(school.id as string)}
                  button
                  dense
                  disableGutters
                  disableRipple={false}
                  css={accountNavItemCss}
                  key={school.id as string}
                  onMouseDown={popover.close}
                >
                  <ListItemIcon>
                    <School />
                  </ListItemIcon>
                  <ListItemText>{school.name}</ListItemText>
                </ListItem>
              ))}

              <Divider />
            </Fragment>
          )}

          {showSwitchCommunityAdministration && (
            <ListItem
              button
              dense
              css={accountNavItemCss}
              onClick={() => {
                popover.close();
                navigate(isAdminRoute ? ROUTES.user.home.index : ROUTES.admin.dashboard.index);
              }}
            >
              <ListItemIcon>
                {isAdminRoute ? <ManageAccounts /> : <SupervisedUserCircle />}
              </ListItemIcon>

              <ListItemText>
                Switch to {isAdminRoute ? 'Member' : 'Community Administration'}
              </ListItemText>
            </ListItem>
          )}

          {showSwitchAdvertising && (
            <ListItem
              component="a"
              href={`${ADVERTISING_DOMAIN}/dashboard?fromCommunity`}
              button
              dense
              css={accountNavItemCss}
            >
              <ListItemIcon>
                <Campaign />
              </ListItemIcon>
              <ListItemText>Switch to Advertising</ListItemText>
            </ListItem>
          )}

          {showSwitchPlatformAdmin && (
            <ListItem
              component="a"
              href={`${ADVERTISING_DOMAIN}/admin`}
              button
              dense
              css={accountNavItemCss}
            >
              <ListItemIcon>
                <AdminPanelSettings />
              </ListItemIcon>
              <ListItemText>Switch to Jebel Administration</ListItemText>
            </ListItem>
          )}

          <Divider />

          <Link
            to={ROUTES.user.settings.information}
            style={{ textDecoration: 'none' }}
            onClick={popover.close}
          >
            <ListItem css={accountNavItemCss} button dense disableGutters>
              <ListItemIcon>
                <AccountCircle />
              </ListItemIcon>

              <ListItemText>My Account</ListItemText>
            </ListItem>
          </Link>

          <ListItem button dense disableGutters css={signOutNavItemCss} onClick={handleLogout}>
            <ListItemIcon>
              <PowerSettingsNew />
            </ListItemIcon>

            <ListItemText>Sign Out</ListItemText>
          </ListItem>
        </MenuList>
      )}
    </Popover>
  );
}
